import styled from '@emotion/styled'
import { Button, TextStylePreset, Typography } from '@vivaldis/antd-ui'
import { Edit } from '@vivaldis/icons'
import { useMaritalStateOptions, useTaxCodeOptions } from '@vivaldis/ui'
import { useTranslation } from 'react-i18next'
import { NotSetValue } from '../NotSetValue'
import { EmployeeProfileQuery_employee } from '../../typings/EmployeeProfileQuery'
import { Information } from '../../../Information'

interface Props {
  employee: EmployeeProfileQuery_employee
}

export const WageProfile = ({ employee }: Props) => {
  const [t] = useTranslation('web_ui')

  const { workProfile } = employee

  const taxCode = workProfile?.taxCode
  const childrenNumber = workProfile?.childrenNumber
  const childrenWithDisabilitiesNumber =
    workProfile?.childrenWithDisabilitiesNumber
  const dependentPersonsNumber = workProfile?.dependentPersonsNumber
  const dependentPersonsWithDisabilitiesNumber =
    workProfile?.dependentPersonsWithDisabilitiesNumber
  const maritalDate = workProfile?.maritalDate
  const maritalState = workProfile?.maritalState
  const involuntaryPartTime = workProfile?.involuntaryPartTime

  const taxCodeOptions = useTaxCodeOptions([taxCode || ''])

  const maritalStateOptions = useMaritalStateOptions({
    maritalState: maritalState
  })

  return (
    <>
      <Base>
        <Typography.Paragraph
          preset={TextStylePreset.Semibold16}
          marginBottom={0}
        >
          {t('employee_profile.wage_profile_header')}
        </Typography.Paragraph>
        <Button
          to={`/employee/${employee.id}/edit-employee-wage-profile`}
          openInSideSheet
          type="default"
          shape="round"
          icon={<Edit />}
        >
          {t('employee_profile.edit_wage_profile_button')}
        </Button>
      </Base>
      <Information>
        <Information.InformationField
          label={t('employee_profile.children_number')}
        >
          {childrenNumber ?? <NotSetValue />}
        </Information.InformationField>
        <Information.InformationField
          label={t('employee_profile.children_with_disabilities_number')}
        >
          {childrenWithDisabilitiesNumber ?? <NotSetValue />}
        </Information.InformationField>
        <Information.InformationField
          label={t('employee_profile.dependent_persons_number')}
        >
          {dependentPersonsNumber ?? <NotSetValue />}
        </Information.InformationField>
        <Information.InformationField
          label={t(
            'employee_profile.dependent_persons_with_disabilities_number'
          )}
        >
          {dependentPersonsWithDisabilitiesNumber ?? <NotSetValue />}
        </Information.InformationField>
        <Information.InformationField
          label={t('employee_profile.marital_state')}
        >
          {maritalState ? maritalStateOptions?.[0]?.label : <NotSetValue />}
        </Information.InformationField>
        <Information.InformationField
          label={t('employee_profile.marital_date')}
        >
          {maritalDate ?? <NotSetValue />}
        </Information.InformationField>

        <Information.InformationField label={t('employee_profile.student')}>
          {employee.isStudent
            ? t('yes', { ns: 'translation' })
            : t('no', { ns: 'translation' })}
        </Information.InformationField>
        <Information.InformationField label={t('employee_profile.tax_code')}>
          {taxCodeOptions?.[0]?.label ?? <NotSetValue />}
        </Information.InformationField>
        <Information.InformationField
          label={t('employee_profile.involuntary_part_time')}
        >
          {involuntaryPartTime === undefined ? (
            <NotSetValue />
          ) : involuntaryPartTime ? (
            t('yes', { ns: 'translation' })
          ) : (
            t('no', { ns: 'translation' })
          )}
        </Information.InformationField>
      </Information>
    </>
  )
}

const Base = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 16px;
`
